import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Phone, CheckCircle2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random()}`);

const WELCOME = "Hi there! 👋 I'm Jasmine, the J&S Air Conditioning assistant. Ask me about our services, brands or service areas — or I can help you request a free quote.";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(uid);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [view, setView] = useState("chat"); // chat | callback | callback-done
  const [lead, setLead] = useState({ name: "", phone: "", message: "" });
  const [leadSending, setLeadSending] = useState(false);
  const [leadError, setLeadError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, view]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having trouble right now. Please call us on (03) 9330 3911 or use the Request a Quote page." }]);
    }
    setSending(false);
  };

  const submitLead = async () => {
    if (leadSending) return;
    if (!lead.name.trim() || !lead.phone.trim()) {
      setLeadError("Please enter your name and phone number.");
      return;
    }
    setLeadError("");
    setLeadSending(true);
    try {
      await axios.post(`${API}/chat/lead`, { session_id: sessionId, ...lead });
      setView("callback-done");
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, that didn't go through. Please call (03) 9330 3911." }]);
      setView("chat");
    }
    setLeadSending(false);
  };

  return (
    <>
      {/* Launcher */}
      <motion.button
        data-testid="chat-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat assistant"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-js-blue text-white shadow-[0_10px_35px_rgba(11,78,162,0.45)] grid place-items-center hover:bg-js-dark transition-colors duration-300"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}><X size={24} /></motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}><MessageCircle size={24} /></motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-8rem)] bg-white rounded-3xl shadow-[0_24px_70px_rgba(0,0,0,0.22)] border border-black/5 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-js-blue text-white px-5 py-4 flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 rounded-full bg-white/15 grid place-items-center font-display font-bold">J</div>
              <div className="flex-1">
                <p className="font-display font-bold leading-tight">J&amp;S Assistant</p>
                <p className="text-white/70 text-xs flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Online now</p>
              </div>
              <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-white/80 hover:text-white"><X size={20} /></button>
            </div>

            {view === "callback-done" ? (
              <div className="flex-1 grid place-items-center p-8 text-center" data-testid="chat-lead-success">
                <div>
                  <span className="grid place-items-center w-16 h-16 rounded-full bg-js-blue/10 text-js-blue mx-auto mb-4"><CheckCircle2 size={36} /></span>
                  <p className="font-display font-bold text-lg">Thanks, {lead.name.split(" ")[0] || "there"}!</p>
                  <p className="text-js-charcoal/60 text-sm mt-2 leading-relaxed">We've received your details and the J&amp;S team will be in touch shortly.</p>
                  <button onClick={() => { setView("chat"); setLead({ name: "", phone: "", message: "" }); }} className="mt-6 inline-flex items-center gap-2 text-js-blue text-sm font-semibold"><ArrowLeft size={15} /> Back to chat</button>
                </div>
              </div>
            ) : view === "callback" ? (
              <div className="flex-1 flex flex-col p-5 overflow-y-auto" data-testid="chat-lead-form">
                <button onClick={() => setView("chat")} className="inline-flex items-center gap-1.5 text-js-charcoal/50 text-sm mb-4 self-start hover:text-js-blue"><ArrowLeft size={15} /> Back</button>
                <p className="font-display font-bold text-lg">Request a callback</p>
                <p className="text-js-charcoal/60 text-sm mt-1 mb-4">Leave your details and we'll call you back.</p>
                <label className="text-xs font-semibold text-js-charcoal mb-1">Name *</label>
                <input data-testid="chat-lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="h-11 px-4 rounded-xl border border-black/10 focus:border-js-blue outline-none mb-3" placeholder="Your name" />
                <label className="text-xs font-semibold text-js-charcoal mb-1">Phone *</label>
                <input data-testid="chat-lead-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="h-11 px-4 rounded-xl border border-black/10 focus:border-js-blue outline-none mb-3" placeholder="e.g. 0400 000 000" />
                <label className="text-xs font-semibold text-js-charcoal mb-1">What do you need? (optional)</label>
                <textarea data-testid="chat-lead-message" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} rows={3} className="p-3 rounded-xl border border-black/10 focus:border-js-blue outline-none mb-4 resize-none" placeholder="Brief details..." />
                {leadError && <p data-testid="chat-lead-error" className="text-js-red text-xs mb-3 -mt-2">{leadError}</p>}
                <button data-testid="chat-lead-submit" onClick={submitLead} disabled={leadSending} className="h-12 rounded-full bg-js-red text-white font-semibold flex items-center justify-center gap-2 hover:bg-js-red/90 transition-colors disabled:opacity-60">
                  {leadSending && <Loader2 size={18} className="animate-spin" />} Request callback
                </button>
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-js-grey" data-testid="chat-messages">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[82%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-js-blue text-white rounded-br-sm" : "bg-white text-js-charcoal border border-black/5 rounded-bl-sm"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start"><div className="bg-white border border-black/5 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                      {[0, 1, 2].map((d) => (<span key={d} className="w-1.5 h-1.5 rounded-full bg-js-charcoal/40 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />))}
                    </div></div>
                  )}
                </div>
                <div className="p-3 border-t border-black/5 bg-white shrink-0">
                  <button data-testid="chat-callback-btn" onClick={() => setView("callback")} className="w-full mb-2 flex items-center justify-center gap-2 text-js-blue text-xs font-semibold py-2 rounded-full bg-js-blue/5 hover:bg-js-blue/10 transition-colors"><Phone size={14} /> Request a callback</button>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="chat-input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && send()}
                      placeholder="Type your question..."
                      className="flex-1 h-11 px-4 rounded-full bg-js-grey border border-black/10 focus:border-js-blue outline-none text-sm"
                    />
                    <button data-testid="chat-send" onClick={send} disabled={sending || !input.trim()} className="w-11 h-11 rounded-full bg-js-blue text-white grid place-items-center hover:bg-js-dark transition-colors disabled:opacity-50 shrink-0">
                      {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
